
































/* eslint-disable @typescript-eslint/camelcase */
import { Component, Ref, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { MetaInfo } from 'vue-meta';

import Wrapper from '@/components/layout/Wrapper.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import GameLogo from '@/components/game/GameLogo.vue';
import EmbedGame from '@/components/common/EmbedGame.vue';
import GameFrame from '@/components/common/GameFrame.vue';
import InstallPrompt from '@/components/common/InstallPrompt.vue';
import PageFooter from '@/components/common/PageFooter.vue';
import XsollaModal from '@/components/xsolla/XsollaModal.vue';

import AuthMixin from '@/mixins/auth';
import IdentityMixin from '@/mixins/identity';
import LocaleMixin from '@/mixins/locale';

import SocialProviders from '@/api/xsolla/social-providers';

import logger from '@/plugins/loglevel';

import { MessageListenerEvent } from '@/helpers/frame';
import { addGtmEvent, setGtmUserId } from '@/helpers/gtm';
import { buildLocationHostUrl } from '@/helpers/core';

import { GAME_EMBED_HS } from '@/config/games';
import { PROVIDER_LINK_URI } from '@/config/xsolla';

import {
  FrameEvent,
  RequestEventParams,
  GTMEventParams,
  LangChangedParams,
  LinkSocialParams,
  OriginIdChangedParams,
  PROVIDERS,
} from '@/constants/frame';

import MARKETING_PARAMS from '@/initializers/analytics';

@Component({
  metaInfo(): MetaInfo {
    return {
      title: this.$t('homescapes.title') as string,
    };
  },
  components: {
    InstallPrompt,
    GameFrame,
    Wrapper,
    PageHeader,
    GameLogo,
    EmbedGame,
    PageFooter,
    XsollaModal,
  },
})
export default class Play extends mixins(AuthMixin, IdentityMixin, LocaleMixin) {
  @Ref() readonly frame!: GameFrame;

  @Ref() readonly auth!: XsollaModal;

  ready = false;

  requestId: string | null = null;

  get link() {
    return GAME_EMBED_HS;
  }

  get getParams() {
    return this.$route.query;
  }

  get marketing() {
    return MARKETING_PARAMS && JSON.stringify(MARKETING_PARAMS);
  }

  get postParams() {
    return {
      locale: this.locale,
      temp_user_id: this.tempUserId,
      access_token: this.authToken,
      marketing: this.marketing,
      event_source_url: buildLocationHostUrl(),
    };
  }

  @Watch('locale')
  localeChanged(langId: string) {
    this.frame.postMessage(FrameEvent.LANG_CHANGED, { langId });
  }

  beforeMount() {
    this.prepareFrame();
  }

  async prepareFrame() {
    this.ready = false;

    return this.prepareToken().finally(() => {
      this.ready = true;
    });
  }

  handleFrameMessage({ code, params }: MessageListenerEvent) {
    logger.debug('Frame message received -->', code, params);

    if (!params) {
      return;
    }

    switch (code) {
      case FrameEvent.LOGIN:
        return this.handleLogin(params as RequestEventParams);
      case FrameEvent.LANG_CHANGED:
        return this.handleLangChanged(params as LangChangedParams);
      case FrameEvent.GTM_EVENT:
        return this.handleGTMEvent(params as GTMEventParams);
      case FrameEvent.LINK_SOCIAL:
        return this.handleLinkSocial(params as LinkSocialParams);
      case FrameEvent.LOGOUT:
        return this.handleLogout();
      case FrameEvent.ORIGIN_ID_CHANGED:
        return this.handleOriginIdChanged(params as OriginIdChangedParams);
      case FrameEvent.RELOAD_FRAME:
        return this.handleReloadFrame();
      default:
        break;
    }
  }

  handleLogin(params: RequestEventParams) {
    if (params) {
      this.requestId = params.requestId;
    }

    this.auth.show();
  }

  handleLogout() {
    this.hardLogout().finally(() => {
      this.$router.push({ name: 'main' });
    });
  }

  handleLangChanged({ langId }: LangChangedParams) {
    this.setLocale(langId);
  }

  handleGTMEvent({ eventName, params }: GTMEventParams) {
    addGtmEvent(eventName, params);
  }

  handleLinkSocial({ provider, requestId }: LinkSocialParams) {
    this.generateLoginUrl(provider)
      .then(url => window.location.replace(url))
      .catch(({ message: error }) => {
        this.frame.postMessage(FrameEvent.LINK_SOCIAL, {
          requestId,
          provider,
          result: {
            error,
            linked: false,
          },
        });
      });
  }

  handleOriginIdChanged({ originId: userId }: OriginIdChangedParams) {
    this.updateTempUserId(userId);

    setGtmUserId(userId, true);
  }

  handleReloadFrame() {
    if (this.checkTokenExpired()) {
      this.prepareFrame();

      return;
    }

    this.frame.renderFrame();
  }

  async generateLoginUrl(code: string): Promise<string> {
    const provider = PROVIDERS[code];

    if (!provider) {
      throw new TypeError('Unknown provider');
    }

    return SocialProviders.loginUrl(provider, PROVIDER_LINK_URI).then(({ data }) => data.url);
  }

  handleAuthClose() {
    this.frame.postMessage(FrameEvent.LOGIN, {
      requestId: this.requestId,
      result: {
        aborted: true,
      },
    });

    this.requestId = null;
  }

  async prepareToken() {
    if (!this.checkTokenExpired()) {
      return;
    }

    const refreshed = await this.refreshToken().catch(() => false);

    if (refreshed) {
      return;
    }

    return this.hardLogout();
  }

  async hardLogout() {
    return this.revokeToken()
      .catch(() => this.resetToken())
      .finally(() => this.refreshIdentity());
  }
}
